import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCurrencySymbol } from '../utils/currency';
import { getMinBudget } from '../utils/budgetRanges';
import ModalCloseButton from './ui/ModalCloseButton';
import { needsPlanReEstimate } from '../utils/planEstimateCore';

const fieldLabelClass = 'text-[11px] font-bold uppercase tracking-wide text-gray-500';
const fieldInputClass =
  'h-11 w-full rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary';

const PlanEventDetailsModal = ({
  isOpen,
  onClose,
  eventData,
  onSaveDetails,
  onReEstimate,
  plannedThemeCount = 1
}) => {
  const country = eventData?.country || 'US';
  const minBudget = getMinBudget(country);
  const currencySymbol = getCurrencySymbol(country);

  const [guestCount, setGuestCount] = useState('');
  const [budget, setBudget] = useState('');
  const [date, setDate] = useState('');
  const [showMultiPlanConfirm, setShowMultiPlanConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setGuestCount(eventData?.guest_count ? String(eventData.guest_count) : '');
    setBudget(eventData?.budget != null ? String(eventData.budget) : '');
    setDate(eventData?.date || eventData?.event_date || '');
    setShowMultiPlanConfirm(false);
    setPendingAction(null);
  }, [isOpen, eventData]);

  const baseline = useMemo(() => ({
    guest_count: Number(eventData?.guest_count || 0),
    budget: Number(eventData?.budget || 0),
    date: eventData?.date || eventData?.event_date || ''
  }), [eventData]);

  const parsedGuests = parseInt(guestCount, 10);
  const parsedBudget = Number(budget);
  const hasValidGuests = Number.isFinite(parsedGuests) && parsedGuests > 0;
  const hasValidBudget = Number.isFinite(parsedBudget) && parsedBudget >= minBudget;

  const payload = useMemo(() => ({
    guest_count: hasValidGuests ? parsedGuests : eventData?.guest_count,
    budget: hasValidBudget ? parsedBudget : eventData?.budget,
    date: date || null,
    event_date: date || null
  }), [hasValidGuests, hasValidBudget, parsedGuests, parsedBudget, date, eventData]);

  const hasChanges = hasValidGuests && hasValidBudget && (
    parsedGuests !== baseline.guest_count
    || parsedBudget !== baseline.budget
    || (date || '') !== (baseline.date || '')
  );

  const pendingReEstimate = needsPlanReEstimate(eventData);
  const canSave = hasChanges;
  const canReEstimate = hasChanges || pendingReEstimate;

  const statusMessage = (() => {
    if (hasChanges) {
      return 'Save keeps your current list. Re-estimate refreshes quantities, timeline, and budget.';
    }
    if (pendingReEstimate) {
      return 'Details were saved without re-estimating. Re-estimate when you are ready to refresh quantities, timeline, and budget.';
    }
    return 'Change at least one field to save or re-estimate.';
  })();

  const runAction = (action) => {
    if (plannedThemeCount > 1 && action === 'reestimate') {
      setPendingAction(action);
      setShowMultiPlanConfirm(true);
      return;
    }
    if (action === 'reestimate') {
      onReEstimate?.(payload);
    } else {
      onSaveDetails?.(payload);
    }
    onClose?.();
  };

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div
        className="relative flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-event-details-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
          <div className="min-w-0 pr-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-600">Plan details</p>
            <h3 id="plan-event-details-title" className="mt-1 text-xl font-extrabold text-gray-950">
              Edit guests, budget, or date
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Update core inputs here. Re-estimate refreshes quantities, timeline, and budget suggestions for this plan.
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <div className="grid grid-cols-2 gap-3">
            <label className="block min-w-0">
              <span className={fieldLabelClass}>Guests</span>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg" aria-hidden="true">
                  👥
                </span>
                <input
                  type="number"
                  min="1"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  placeholder="e.g. 30"
                  className={`${fieldInputClass} pl-10 pr-3`}
                />
              </div>
            </label>

            <label className="block min-w-0">
              <span className={fieldLabelClass}>Budget</span>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg" aria-hidden="true">
                  💰
                </span>
                <input
                  type="number"
                  min={minBudget}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  onBlur={() => {
                    if (budget && Number(budget) < minBudget) setBudget(String(minBudget));
                  }}
                  placeholder={`e.g. ${minBudget}`}
                  className={`${fieldInputClass} pl-10 pr-9`}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  {currencySymbol}
                </span>
              </div>
            </label>
          </div>

          <label className="mt-3 block w-full max-w-[11.5rem]">
            <span className={fieldLabelClass}>
              Event date{' '}
              <span className="font-semibold normal-case tracking-normal text-gray-400">(optional)</span>
            </span>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg" aria-hidden="true">
                📅
              </span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`${fieldInputClass} pl-10 pr-2`}
              />
            </div>
          </label>
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:px-6">
          <div
            className={`mb-3 flex min-h-14 items-center rounded-xl border px-3 py-2 ${
              pendingReEstimate && !hasChanges
                ? 'border-amber-200 bg-amber-50'
                : 'border-gray-200 bg-white'
            }`}
            role="status"
            aria-live="polite"
          >
            <p className={`text-xs leading-relaxed ${pendingReEstimate && !hasChanges ? 'text-amber-900' : 'text-gray-600'}`}>
              {statusMessage}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end sm:gap-2">
            <button
              type="button"
              disabled={!canSave}
              onClick={() => runAction('save')}
              className="order-1 flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-800 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400 sm:order-2 sm:w-auto sm:px-4"
            >
              <span className="sm:hidden">Save</span>
              <span className="hidden sm:inline">Save details only</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="order-2 flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 transition hover:bg-gray-50 sm:order-1 sm:mr-auto sm:w-auto sm:px-4"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canReEstimate}
              onClick={() => runAction('reestimate')}
              className="order-3 col-span-2 flex h-11 items-center justify-center rounded-xl bg-zinc-900 px-4 text-sm font-bold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300 sm:col-span-1 sm:w-auto"
            >
              Re-estimate plan
            </button>
          </div>
        </div>

        {showMultiPlanConfirm && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 p-5 backdrop-blur-sm">
            <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
              <h4 className="text-lg font-extrabold text-gray-950">Apply to other theme plans?</h4>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                You have {plannedThemeCount} theme plans saved. Guest and budget changes update event details everywhere. Re-estimate only refreshes the plan you are viewing.
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowMultiPlanConfirm(false);
                    setPendingAction(null);
                  }}
                  className="flex h-10 w-full items-center justify-center rounded-xl text-sm font-semibold text-gray-600 transition hover:bg-gray-100 sm:mr-auto sm:w-auto"
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (pendingAction === 'reestimate') onReEstimate?.(payload);
                    else onSaveDetails?.(payload);
                    onClose?.();
                  }}
                  className="flex h-12 w-full items-center justify-center rounded-2xl bg-zinc-900 text-sm font-bold text-white sm:w-auto sm:px-6"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PlanEventDetailsModal;
